import React from 'react';
import { useNavigate } from 'react-router-dom';
import { University } from '../../../types';
import { useApplicationRequirements } from '../../../services/universityService';

interface RequirementsTabProps {
  university: University;
}

const RequirementsTab: React.FC<RequirementsTabProps> = ({ university }) => {
  const navigate = useNavigate();
  // Fetch application requirements data
  const { data: appRequirements, isLoading: isLoadingRequirements, error: requirementsError } = useApplicationRequirements(
    university.id
  );
  const handleViewDetails = () => {
    navigate(`/analyze/${university.id}`);
  };

  // Format currency
  const formatCurrency = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 'N/A';
    return `$${num.toLocaleString()}`;
  };

  return (
    <div className="tab-content">
      <h3 className="text-3xl font-bold mb-8" style={{ color: 'var(--text-primary)' }}>Application Requirements</h3>
      
      {isLoadingRequirements ? (
        <div className="space-y-6">
          {/* Loading skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl p-6 animate-pulse" style={{ background: 'var(--surface-hover)' }}>
                <div className="h-6 bg-gray-200 rounded mb-4"></div>
                <div className="h-16 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      ) : requirementsError ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <div className="text-red-500 text-xl mb-2">
            <i className="fas fa-exclamation-circle"></i>
          </div>
          <h4 className="text-lg font-semibold text-red-800 mb-2">Error Loading Requirements</h4>
          <p className="text-red-600">Unable to load application requirements. Please try again later.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Top Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Application Platforms Card */}
            <div className="bg-blue-50/50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <div className="text-blue-500 text-2xl mr-3">
                  <i className="fas fa-file-alt"></i>
                </div>
                <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Application Platforms</h4>
              </div>
              <div className="space-y-2">
                {[
                  { key: 'Common Application', label: 'Common App' },
                  { key: 'Coalition Application', label: 'Coalition App' },
                  { key: 'QUEST_BRIDGE_APPLICATION', label: 'Questbridge App' }
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between p-2 rounded-lg" style={{ background: 'rgba(var(--surface-primary-rgb, 255, 255, 255), 0.6)' }}>
                    <span className="font-medium text-sm" style={{ color: 'var(--text-secondary)' }}>{label}</span>
                    <span className={`font-bold text-sm ${
                      appRequirements?.uni_app_type_mapping[key] 
                        ? 'text-green-600' 
                        : 'text-red-600'
                    }`}>
                      {appRequirements?.uni_app_type_mapping[key] ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Writing Supplements Card */}
            <div className="bg-green-50/50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <div className="text-green-500 text-2xl mr-3">
                  <i className="fas fa-pen-fancy"></i>
                </div>
                <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Writing Supplements</h4>
              </div>
              <div className="text-center mb-3">
                <p className="text-3xl font-bold text-green-600">
                  {appRequirements?.min_supplements_required || 0}
                </p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Required Essays</p>
              </div>
              <div className="flex justify-center">
                <button 
                  onClick={handleViewDetails}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                >
                  View Details
                </button>
              </div>
            </div>

            {/* Application Fee Card */}
            <div className="bg-orange-50/50 rounded-xl p-6 flex flex-col items-center justify-center">
              <div className="text-orange-500 text-3xl mb-3">
                <i className="fas fa-dollar-sign"></i>
              </div>
              <h5 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Application Fee</h5>
              <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {formatCurrency(university.application_fee)}
              </p>
            </div>
          </div>

          {/* Middle Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* SAT/ACT Card */}
            <div className="rounded-xl p-6 flex flex-col items-center justify-center" style={{ background: 'var(--surface-hover)' }}>
              <div className="text-blue-500 text-3xl mb-3">
                <i className="fas fa-chart-bar"></i>
              </div>
              <h5 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>SAT/ACT</h5>
              <p className="text-sm text-yellow-600 font-semibold">
                {university.sat_act_status || 'Considered but not required'}
              </p>
            </div>

            {/* Demonstrated Interest Card */}
            <div className="rounded-xl p-6 flex flex-col items-center justify-center" style={{ background: 'var(--surface-hover)' }}>
              <div className="text-purple-500 text-3xl mb-3">
                <i className="fas fa-handshake"></i>
              </div>
              <h5 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Demonstrated Interest</h5>
              <p className="text-sm text-orange-600 font-semibold mb-3">Heavily Considered</p>
              <button className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors">
                View Opportunities
              </button>
            </div>

            {/* GPA Status Card */}
            <div className="rounded-xl p-6 flex flex-col items-center justify-center" style={{ background: 'var(--surface-hover)' }}>
              <div className="text-green-500 text-3xl mb-3">
                <i className="fas fa-graduation-cap"></i>
              </div>
              <h5 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Highschool GPA</h5>
              <p className="text-sm text-green-600 font-semibold">
                {university.high_school_gpa || 'Required'}
              </p>
            </div>
          </div>

          {/* SAT/ACT Detailed Section */}
          <div className="border rounded-xl p-6" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center mb-6">
              <div className="text-blue-500 text-2xl mr-3">
                <i className="fas fa-chart-bar"></i>
              </div>
              <div className="flex items-center justify-between w-full">
                <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>SAT/ACT Score Ranges</h4>
                <span className="bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full font-semibold text-sm">
                  {university.sat_act_status || 'Considered but not required'}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center p-4 bg-blue-50/50 rounded-lg">
                <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Average SAT Range</h5>
                <p className="text-2xl font-bold text-blue-600">
                  {university.sat_range_min && university.sat_range_max 
                    ? `${university.sat_range_min}-${university.sat_range_max}`
                    : 'N/A'
                  }
                </p>
              </div>
              <div className="text-center p-4 bg-blue-50/50 rounded-lg">
                <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Average ACT Range</h5>
                <p className="text-2xl font-bold text-blue-600">
                  {university.act_range_min && university.act_range_max 
                    ? `${university.act_range_min}-${university.act_range_max}`
                    : 'N/A'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequirementsTab;
